import React, { useCallback, useRef, useEffect, useMemo, ClipboardEvent } from 'react';
import { Autocomplete, TextField, Chip, Box } from '@mui/material';
import {
  GridFilterOperator,
  GridFilterInputValueProps,
  getGridStringOperators,
} from '@mui/x-data-grid-premium';

/**
 * Custom filter input for string "isAnyOf" operator.
 *
 * Supports pasting multiple values separated by newlines, tabs, or spaces
 * (e.g., values copied from Excel or a text list) — each value becomes a
 * separate filter chip, just like Dynamics 365 Finance & Operations.
 */
function StringMultiValueFilterInput(props: GridFilterInputValueProps) {
  const { item, applyValue, focusElementRef } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (typeof focusElementRef === 'function') {
      focusElementRef(inputRef.current);
    } else if (focusElementRef && typeof focusElementRef === 'object') {
      (focusElementRef as React.MutableRefObject<HTMLInputElement | null>).current =
        inputRef.current;
    }
  }, [focusElementRef]);

  const currentValues: string[] = useMemo(() => {
    if (!item.value) return [];
    if (Array.isArray(item.value)) return item.value;
    if (typeof item.value === 'string') return item.value.split(',').filter(Boolean);
    return [];
  }, [item.value]);

  const handleChange = useCallback(
    (_event: unknown, newValues: string[]) => {
      const cleaned = [...new Set(newValues.map((v) => v.trim()).filter(Boolean))];
      applyValue({ ...item, value: cleaned });
    },
    [applyValue, item],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      const pastedText = event.clipboardData.getData('text');
      if (!pastedText) return;

      const newValues = splitPastedText(pastedText);

      if (newValues.length > 1) {
        event.preventDefault();
        const merged = [...new Set([...currentValues, ...newValues])];
        applyValue({ ...item, value: merged });
      }
    },
    [applyValue, item, currentValues],
  );

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={currentValues}
      onChange={handleChange}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={option} size="small" {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label="Value"
          variant="standard"
          placeholder="Type or paste values"
          size="small"
          sx={{ mt: '8px' }}
          onPaste={handlePaste as any}
        />
      )}
      sx={{ minWidth: 200 }}
    />
  );
}

/**
 * HOC that wraps any existing filter InputComponent with a paste listener.
 * When a multi-line paste is detected, it switches the operator to "isAnyOf"
 * and splits the pasted text into individual values.
 *
 * The original component renders exactly as before (same theming/alignment),
 * we just add an invisible paste intercept on a wrapping element.
 */
function withPasteDetection(
  OriginalComponent: React.ComponentType<GridFilterInputValueProps>,
): React.ComponentType<GridFilterInputValueProps> {
  const Wrapped = (props: GridFilterInputValueProps) => {
    const { item, applyValue } = props;

    const handlePaste = useCallback(
      (event: ClipboardEvent<HTMLDivElement>) => {
        const pastedText = event.clipboardData.getData('text');
        if (!pastedText) return;

        const values = splitPastedText(pastedText);

        if (values.length > 1) {
          event.preventDefault();
          event.stopPropagation();
          applyValue({ ...item, operator: 'isAnyOf', value: values });
        }
      },
      [applyValue, item],
    );

    return (
      <Box onPaste={handlePaste as any} sx={{ display: 'contents' }}>
        <OriginalComponent {...props} />
      </Box>
    );
  };
  Wrapped.displayName = `PasteAware(${OriginalComponent.displayName || OriginalComponent.name || 'Component'})`;
  return Wrapped;
}

/**
 * Split pasted text into individual values.
 * Handles newlines (Excel columns), tabs (Excel rows).
 */
function splitPastedText(text: string): string[] {
  return text
    .split(/[\n\r\t]+/)
    .map((v) => v.trim())
    .filter(Boolean);
}

/**
 * Returns the built-in string filter operators with:
 * - All single-value text inputs wrapped with paste detection that auto-switches to isAnyOf
 * - The isAnyOf input replaced with a chip-based multi-value input supporting paste
 */
export function getStringFilterOperators(): GridFilterOperator[] {
  return getGridStringOperators(true).map((op) => {
    if (op.value === 'isAnyOf') {
      return {
        ...op,
        InputComponent: StringMultiValueFilterInput as any,
      };
    }

    // Wrap single-value text inputs with paste detection — preserves original rendering
    if (op.requiresFilterValue !== false && op.InputComponent) {
      return {
        ...op,
        InputComponent: withPasteDetection(op.InputComponent as any) as any,
      };
    }

    return op;
  });
}
